import {Component, OnInit, ViewChild} from '@angular/core';
import {DateAdapter} from '@angular/material/core';
import {MatSidenav} from '@angular/material/sidenav';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {GtProducts} from '@app/models/enums/gt-products.enum';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtCommandService} from '@app/services/gt-command.service';
import {GtConfigurationService} from '@app/services/gt-configuration.service';
import {GtManageOrganizationService} from '@app/services/gt-manage-organization.service';
import {AppUtils} from '@app/shared/utils/app-utils';
import {LangItem, LangsUtil} from '@app/shared/utils/langs-util';
import {environment} from '@environment';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import {LocaleSpecification} from 'moment';
import {forkJoin, Observable} from 'rxjs';
import {filter, map, switchMap, tap} from 'rxjs/operators';
import {DemoService} from './services/demo.service';
import {GtNotificationService} from './services/gt-notification.service';

@Component({
  selector: 'gt-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  languages: LangItem[];
  products = GtProducts;
  isOnSwbPage = false;
  hasUserSettings = environment.hasUserSettings;
  hasAdmin = environment.hasAdmin;
  hasHideStatistics = environment.hasHideStatistics;
  hasServiceProvider = environment.hasServiceProvider;
  showBreadcrumbs = environment.showBreadcrumbs;
  newSwitchboardLink = environment.newSwitchboardLink;
  desktopView: boolean = !AppUtils.isOnMobile();
  managedOrganization: Observable<string>;

  @ViewChild(MatSidenav, {static: true}) sideNav: MatSidenav;

  constructor(
    public authenticationService: GtAuthenticationService,
    private snackBar: MatSnackBar,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private manageOrganizationService: GtManageOrganizationService,
    private configurationService: GtConfigurationService,
    private notificationService: GtNotificationService,
    private translateService: TranslateService,
    private dateAdapter: DateAdapter<any>,
    private commandService: GtCommandService,
    demoService: DemoService
  ) {
    document.title = environment.productName;
    if (!environment.production) {
      demoService.setupDemoPush();
    }

    if (this.desktopView && 'Notification' in window) {
      Notification.requestPermission();
    }

    this.updateMomentLocale();
    const languageString = LangsUtil.getLangItem(environment.languages[0]).ISO_639_1;
    translateService.setDefaultLang(languageString);
    translateService.use(languageString);

    this.commandService.commands.subscribe(c => {
      switch (c.name) {
        case GtCommandService.NAVIGATE_USER:
          this.router.navigate(['user']);
          break;
        case GtCommandService.NAVIGATE_SWITCHBOARD:
          this.router.navigate(['swb']);
          break;
        case GtCommandService.NAVIGATE_SWITCHBOARD_SMS:
          this.router.navigate(['sms']);
          break;
        case GtCommandService.NAVIGATE_SWITCHBOARD_STAT:
          this.router.navigate(['swb/statistics']);
          break;
        case GtCommandService.NAVIGATE_ADMIN:
          this.router.navigate(['admin']);
          break;
        case GtCommandService.NAVIGATE_WALLBOARD:
          this.router.navigate(['wallboard']);
          break;
        case GtCommandService.NAVIGATE_CONFERENCE:
          this.router.navigate(['conference']);
          break;
        case GtCommandService.NAVIGATE_PHONE:
          this.router.navigate(['phone']);
          break;
      }
    });

    this.notificationService.errorNotification.subscribe(error => {
      // maybe further logging like sentry.io
      snackBar.dismiss();
      snackBar.open(error, '', {duration: 5000, panelClass: ['error-background']});
    });

    this.notificationService.clickableNotification.subscribe(notification => {
      snackBar.dismiss();
      const ref = snackBar.open(notification.message, 'Accept', {duration: 5000});
      ref.onAction().subscribe(notification.action);
    });

    this.notificationService.normalNotification.subscribe(notification => {
      snackBar.dismiss();
      snackBar.open(notification, '', {duration: 5000});
    });
    this.authenticationService.isloggedIn().subscribe((isloggedIn: boolean) => {
      if (isloggedIn) {
        this.getLanguages();
      }
    });
    router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe((event: NavigationStart) => {
      if (event.url) {
        console.log('NavigationStart', event.url);
        const isOnLoginPage = event.url === '/' || event.url.indexOf('login') > -1;
        this.isOnSwbPage = event.url.indexOf('swb') > -1;

        if (isOnLoginPage && this.authenticationService.loggedIn) {
          console.log('Already logged in => Go to default page');

          forkJoin([
            this.authenticationService.hasOneOf(GtProducts.VPN_SERVICE_PROVIDER_ROLE),
            this.authenticationService.hasAllOf(GtProducts.SWB_PRODUCT, GtProducts.ATTENDANT_ROLE),
            this.authenticationService.hasOneOf(GtProducts.VPN_ADMINISTRATOR_ROLE)
          ]).subscribe(result => {
            const isServiceProvider = result[0];
            const isSwbUser = result[1];
            const isAdmin = result[2];
            if (isServiceProvider) {
              this.router.navigate([environment.postLoginRedirectRouteServiceProvider]);
            } else if (isSwbUser && isAdmin && environment.postLoginRedirectAdminAndSwb) {
              this.router.navigate([environment.postLoginRedirectAdminAndSwb]);
            } else if (isSwbUser) {
              this.router.navigate([environment.postLoginSwb]);
            } else if (isAdmin) {
              this.router.navigate([environment.postLoginRedirectAdmin]);
            } else {
              this.router.navigate([environment.postLoginRedirectRoute]);
            }
          });
        }
        this.sideNav.close();
      }
    });
  }

  ngOnInit() {
    // listen for changes to managed organization name and login in query string
    this.authenticationService
      .isAdmin()
      .pipe(
        filter(isAdmin => isAdmin),
        switchMap(() => this.authenticationService.hasOneOf(GtProducts.VPN_SERVICE_PROVIDER_ROLE)),
        filter(isServiceProvider => isServiceProvider), // if user is not service provider, end the pipeline here
        switchMap(() => this.activatedRoute.queryParamMap),
        map(p => ({
          oname: p.get('contactOname') ?? this.authenticationService.oname,
          login: p.get('contactLoginStr') ?? this.authenticationService.login
        })),
        tap(({oname, login}) => {
          this.manageOrganizationService.manageOname = oname;
          this.manageOrganizationService.manageLogin = login;
        })
      )
      .subscribe();

    this.managedOrganization = this.manageOrganizationService.organization$.pipe(
      map(org => (org.id !== this.authenticationService.oname ? org.name : null))
    );
  }

  logout() {
    this.authenticationService.doLogout().subscribe(() => {
      this.notificationService.normalNotification.next(this.translateService.instant('logout.successful'));
    });
  }

  hasOneOf(parameters: GtProducts[]): Observable<boolean> {
    return this.authenticationService.hasOneOf(...parameters);
  }

  hasAllOf(parameters: GtProducts[]): Observable<boolean> {
    return this.authenticationService.hasAllOf(...parameters);
  }

  getLanguages() {
    const userData = this.authenticationService.userData;
    const preferredLanguage = userData && userData.language ? userData.language : environment.languages[0];
    this.languages = LangsUtil.getLangItems(environment.languages || []);
    const preferredLanguageItem = this.languages.find(item => item.BCP_47 === preferredLanguage);
    if (preferredLanguageItem) {
      this.translateService.use(preferredLanguageItem.ISO_639_1);
      moment.locale(preferredLanguageItem.BCP_47);
      this.dateAdapter.setLocale(moment.locale());
    }
  }

  setLanguage(language: LangItem) {
    this.authenticationService.userData.language = language.BCP_47;
    localStorage.setItem('user', JSON.stringify(this.authenticationService.userData));
    this.translateService.use(language.ISO_639_1);
    moment.locale(language.BCP_47);
    this.dateAdapter.setLocale(moment.locale());

    this.configurationService
      .setPreferredLanguage(this.authenticationService.oname, this.authenticationService.login, language.BCP_47)
      .subscribe(
        data => {
          if (data.code === 200) {
            console.log('setPreferredLanguage done');
          } else {
            console.log('setPreferredLanguage failed', data);
          }
        },
        error => console.log('setPreferredLanguage', error)
      );
  }

  private updateMomentLocale() {
    moment.updateLocale('nb', {
      durationLabelsStandard: {
        S: 'millisekund',
        SS: 'millisekunder',
        s: 'sekund',
        ss: 'sekunder',
        m: 'minutt',
        mm: 'minutter',
        h: 'time',
        hh: 'timer',
        d: 'dag',
        dd: 'dager',
        w: 'uke',
        ww: 'uker',
        M: 'måned',
        MM: 'måneder',
        y: 'år',
        yy: 'år'
      },
      durationLabelsShort: {
        S: 'msek',
        SS: 'msek',
        s: 'sek',
        ss: 'sek',
        m: 'min',
        mm: 'min',
        h: 't',
        hh: 't',
        d: 'dg.',
        dd: 'dg.',
        w: 'uke',
        ww: 'uker',
        M: 'md.',
        MM: 'md.',
        y: 'år',
        yy: 'år'
      },
      durationTimeTemplates: {
        HMS: 'h:mm:ss',
        HM: 'h:mm',
        MS: 'm:ss'
      },
      durationLabelTypes: [
        {type: 'standard', string: '__'},
        {type: 'short', string: '_'}
      ],
      durationPluralKey: (token, integerValue, decimalValue) => {
        // Singular for a value of `1`, but not for `1.0`.
        if (integerValue === 1 && decimalValue === null) {
          return token;
        }

        return token + token;
      }
    } as LocaleSpecification);
  }
}
